import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";

interface EventBannerProps {
  imageSrc: string;
  alt?: string;
  storageKey?: string;
  pdfUrl?: string;
}

const EventBanner = ({
  imageSrc,
  alt = "Event Banner",
  storageKey = "event_banner_closed",
  pdfUrl = "/event-info.pdf",
}: EventBannerProps) => {
  const isEnabled = process.env.REACT_APP_BANNER_ENABLED === "true";
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isEnabled) return;
    const closed = localStorage.getItem(storageKey);
    setIsOpen(!closed);
  }, [isEnabled, storageKey]);

  if (!isEnabled) return null;

  return (
    <>
      {/* ================= MODAL ================= */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="fixed inset-0 z-[80] flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => {
                localStorage.setItem(storageKey, "true");
                setIsOpen(false);
              }}
            />

            {/* Modal Card */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 30 }}
              transition={{ duration: 0.45, ease: "easeOut" }}
              className="
                relative z-10
                w-[92vw]
                sm:w-[85vw]
                md:w-[70vw]
                lg:w-[55vw]
                max-w-4xl
                max-h-[85vh]
                rounded-2xl
                overflow-hidden
                shadow-2xl
                bg-transparent
              "
            >
              {/* Close Button */}
              <button
                onClick={() => {
                  localStorage.setItem(storageKey, "true");
                  setIsOpen(false);
                }}
                className="
                  absolute top-3 right-3 z-20
                  w-9 h-9 rounded-full
                  bg-black/70 text-white
                  flex items-center justify-center
                  hover:scale-110 transition
                "
                aria-label="Close banner"
              >
                ✕
              </button>

              {/* Banner Image */}
              <img
                src={imageSrc}
                alt={alt}
                className="
                  w-full
                  h-auto
                  max-h-[65vh]
                  object-contain
                "
              />

              {/* ================= ACTIONS ================= */}
              <div className="p-5 flex justify-center gap-4 bg-background">
                <a
                  href={pdfUrl}
                  download
                  className="
      inline-flex items-center gap-2
      px-4 py-2
      sm:px-5 sm:py-2.5
      md:px-6 md:py-3
      rounded-full
      bg-primary text-primary-foreground
      text-xs sm:text-sm md:text-base lg:text-lg
      font-medium
      shadow-lg
      hover:scale-105
      transition
    "
                >
                  Click here for more info
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ================= FLOATING BUTTON ================= */}
      <AnimatePresence>
        {!isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="fixed bottom-6 right-6 z-[70]"
          >
            <motion.button
              onClick={() => setIsOpen(true)}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
              animate={{
                opacity: [1, 0.96, 1],
                scale: [1, 1.045, 1],
                boxShadow: [
                  "0 10px 25px rgba(0,0,0,0.15)",
                  "0 14px 32px rgba(0,0,0,0.22)",
                  "0 10px 25px rgba(0,0,0,0.15)",
                ],
              }}
              transition={{
                duration: 4.5, // slow = calm
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className="
    flex items-center gap-3
    px-5 py-3
    rounded-full
    bg-primary text-primary-foreground
    text-sm font-medium
    shadow-xl
    backdrop-blur
  "
              aria-label="Open event banner"
            >
              <span className="block w-2 h-2 rounded-full bg-white/90" />
              <span>Special Event</span>
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export { EventBanner };
